// src/features/rrhh/horarios/components/EditarTurnoDialog.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, MenuItem, Typography, IconButton, Box, Chip
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";

/* =========================
   Helpers (ids / normalizers)
   ========================= */
function getDetalleId(sh) {
  return sh?.id ?? sh?.detalle_id ?? sh?.horario_detalle_id ?? sh?.id_detalle ?? null;
}
function getHorarioId(sh) {
  return sh?.horario_id ?? sh?.horarioId ?? sh?.id_horario ?? null;
}
function getEmpleadoId(sh) {
  return sh?.empleado_id ?? sh?.empleadoId ?? sh?.employee_id ?? sh?.id_empleado ?? null;
}
function getDiaSemana(sh) {
  const d = sh?.dia_semana ?? sh?.diaSemana ?? sh?.day_of_week ?? sh?.dayOfWeek ?? null;
  const n = Number(d);
  return Number.isFinite(n) ? n : null;
}

function getIni(sh) {
  return sh?.hora_inicio || sh?.hora_entrada || sh?.horaEntrada || sh?.start || "";
}
function getFin(sh) {
  return sh?.hora_fin || sh?.hora_salida || sh?.horaSalida || sh?.end || "";
}

function normalizeTipoDia(sh) {
  const raw = sh?.tipo_dia || sh?.tipoDia || sh?.tipo_dia_enum || "";
  const u = String(raw || "").toUpperCase();
  if (u === "NORMAL" || u === "FRANCO" || u === "FERIADO" || u === "NINGUNO") return u;
  return "NORMAL";
}
function normalizeTipoHorario(sh) {
  const raw = sh?.tipo_horario || sh?.tipoHorario || sh?.tipo || sh?.schedule || sh?.turno || "";
  const u = String(raw || "").toUpperCase();
  if (u === "DIURNO" || u === "NOCTURNO" || u === "MIXTO") return u;
  if (u.includes("DIUR")) return "DIURNO";
  if (u.includes("NOC")) return "NOCTURNO";
  if (u.includes("MIX")) return "MIXTO";
  return "";
}

function hhmm(v) {
  if (!v) return "";
  return String(v).slice(0, 5);
}
function toHHMMSS(v) {
  const s = String(v || "").trim();
  if (!s) return undefined;
  if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  return undefined;
}

function timeToMin(hm) {
  const s = String(hm || "").slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function sortByIni(a, b) {
  return (timeToMin(a?.hora_inicio) ?? 0) - (timeToMin(b?.hora_inicio) ?? 0);
}

function makeTurnoFromShift(sh) {
  return {
    uid: String(getDetalleId(sh) ?? `tmp-${Math.random().toString(16).slice(2)}`),
    id: (() => {
      const n = Number(getDetalleId(sh));
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    hora_inicio: hhmm(getIni(sh)),
    hora_fin: hhmm(getFin(sh)),
  };
}

function shallowEqTurnos(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const A = a[i], B = b[i];
    if ((A?.id ?? null) !== (B?.id ?? null)) return false;
    if ((A?.hora_inicio ?? "") !== (B?.hora_inicio ?? "")) return false;
    if ((A?.hora_fin ?? "") !== (B?.hora_fin ?? "")) return false;
  }
  return true;
}

/* =========================
   Component
   ========================= */
export default function EditarTurnoDialog({
  open,
  onClose,
  shift,
  relatedShifts = [],     // 👈 PASÁ ACÁ los turnos del mismo día (máx 2 normalmente)
  onConfirmUpdate,        // (payload) => void
}) {
  // Meta (para crear/eliminar)
  const meta = useMemo(() => {
    const base = shift || relatedShifts?.[0] || null;
    return {
      horario_id: getHorarioId(base),
      empleado_id: getEmpleadoId(base),
      dia_semana: getDiaSemana(base),
    };
  }, [shift, relatedShifts]);

  const [tipo_dia, setTipoDia] = useState("NORMAL");
  const [tipo_horario, setTipoHorario] = useState("");

  // 1..2 turnos editables
  const [turnos, setTurnos] = useState([]); // [{uid,id?,hora_inicio,hora_fin}]
  const [removedIds, setRemovedIds] = useState([]); // ids a borrar (si quitás turno existente)
  const [err, setErr] = useState("");

  // snapshot original para detectar "no changes"
  const originalRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setErr("");

    const base = shift || relatedShifts?.[0] || null;

    const initialTipoDia = normalizeTipoDia(base);
    const initialTipoHorario = normalizeTipoHorario(base);

    const list = (relatedShifts?.length ? relatedShifts : [shift]).filter(Boolean);

    const initialTurnos = list
      .map(makeTurnoFromShift)   // <-- crea {uid,id,hora_inicio,hora_fin}
      .sort(sortByIni);

    // fallback: si por algún motivo no vino nada, dejá 1 turno editable
    const safeTurnos =
      initialTurnos.length > 0
        ? initialTurnos
        : [
            {
              uid: `tmp-${Date.now()}`,
              id: null,
              hora_inicio: "09:00",
              hora_fin: "13:00",
            },
          ];

    // snapshot ORIGINAL coherente con el state del dialog
    originalRef.current = {
      tipo_dia: initialTipoDia,
      tipo_horario: initialTipoHorario || "",
      turnos: safeTurnos.map((t) => ({
        id: t.id ?? null,
        hora_inicio: t.hora_inicio || "",
        hora_fin: t.hora_fin || "",
      })),
      removed_ids: [],
    };

    // state del dialog
    setTipoDia(initialTipoDia);
    setTipoHorario(initialTipoHorario || "");
    setTurnos(safeTurnos);
    setRemovedIds([]);
  }, [open, shift, relatedShifts]);


  const disableTurnos = String(tipo_dia || "NORMAL").toUpperCase() !== "NORMAL";

  function addTurno() {
    if (disableTurnos) return;
    setErr("");
    setTurnos((prev) => {
      if (prev.length >= 2) return prev;
      return [
        ...prev,
        {
          uid: `tmp-${Date.now()}`,
          id: null,
          hora_inicio: "17:00",
          hora_fin: "21:00",
        },
      ];
    });
  }

  function removeTurno(idx) {
    if (disableTurnos) return;
    setErr("");
    setTurnos((prev) => {
      if (prev.length <= 1) return prev; // mínimo 1
      const t = prev[idx];
      if (t?.id) setRemovedIds((r) => [...r, t.id]); // marcar para borrar si existe
      return prev.filter((_, i) => i !== idx);
    });
  }

  function updateTurno(idx, patch) {
    setErr("");
    setTurnos((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t))
    );
  }

  function validate() {
    const nextTipo = String(tipo_dia || "NORMAL").toUpperCase();

    // si NO es NORMAL, no exigimos turnos
    if (nextTipo !== "NORMAL") return "";

    if (!turnos.length) return "Debe haber al menos 1 turno.";

    // horas completas
    for (const t of turnos) {
      if (!hhmm(t.hora_inicio) || !hhmm(t.hora_fin)) {
        return "Cada turno debe tener hora de inicio y fin.";
      }
    }

    // solapamiento (máx 2)
    const sorted = [...turnos].slice().sort((a, b) =>
      (timeToMin(a.hora_inicio) ?? 0) - (timeToMin(b.hora_inicio) ?? 0)
    );

    if (sorted.length === 2) {
      const aS = timeToMin(sorted[0].hora_inicio);
      const aE = timeToMin(sorted[0].hora_fin);
      const bS = timeToMin(sorted[1].hora_inicio);
      const bE = timeToMin(sorted[1].hora_fin);

      if (aS == null || aE == null || bS == null || bE == null) {
        return "Formato de hora inválido.";
      }

      if (aE <= aS || bE <= bS) return "La hora fin debe ser mayor que la hora inicio.";
      if (aE > bS) return "Los turnos no pueden superponerse (el 1er turno debe terminar antes de que empiece el 2do).";
    } else {
      const s = timeToMin(sorted[0].hora_inicio);
      const e = timeToMin(sorted[0].hora_fin);
      if (s == null || e == null) return "Formato de hora inválido.";
      if (e <= s) return "La hora fin debe ser mayor que la hora inicio.";
    }

    return "";
  }

  const canApply = Boolean(meta?.empleado_id && meta?.dia_semana);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Editar turnos del día</DialogTitle>

      <DialogContent dividers>
        {/* Info mínima */}
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`Emp: ${meta?.empleado_id ?? "-"}`} />
          <Chip size="small" label={`Día: ${meta?.dia_semana ?? "-"}`} />
          {meta?.horario_id ? <Chip size="small" label={`Horario: ${meta.horario_id}`} /> : null}
        </Stack>

        {err ? (
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "error.main" }}>
              {err}
            </Typography>
          </Box>
        ) : null}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tipo día"
            select
            value={tipo_dia}
            onChange={(e) => {
              const next = e.target.value;
              const nextU = String(next).toUpperCase();
              setTipoDia(next);

              if (nextU !== "NORMAL") {
                setErr("");
              
                setTurnos((prev) => {
                  // me quedo con el primer turno como “ancla”
                  const first = prev?.[0] || { uid: `tmp-${Date.now()}`, id: null, hora_inicio: "00:00", hora_fin: "00:01" };
                
                  // si había un segundo turno existente, lo marco para borrar
                  const extraIds = (prev || []).slice(1).map(t => t?.id).filter(Boolean);
                  if (extraIds.length) {
                    setRemovedIds((r) => Array.from(new Set([...(r || []), ...extraIds])));
                  }
                
                  // dejo 1 turno placeholder
                  return [{ ...first, hora_inicio: "00:00", hora_fin: "00:01" }];
                });
              
                return;
              }
            
              // vuelve a NORMAL
              setTurnos((prev) => (prev?.length ? prev : [{
                uid: `tmp-${Date.now()}`,
                id: null,
                hora_inicio: "09:00",
                hora_fin: "13:00",
              }]));
            }}
            fullWidth
          >
            <MenuItem value="NORMAL">Normal</MenuItem>
            <MenuItem value="FRANCO">Franco</MenuItem>
            <MenuItem value="NINGUNO">Ninguno</MenuItem>
          </TextField>

          <TextField
            label="Tipo horario"
            select
            value={tipo_horario}
            onChange={(e) => setTipoHorario(e.target.value)}
            fullWidth
            disabled={disableTurnos}
          >
            <MenuItem value="">(sin definir)</MenuItem>
            <MenuItem value="DIURNO">Diurno</MenuItem>
            <MenuItem value="NOCTURNO">Nocturno</MenuItem>
            <MenuItem value="MIXTO">Mixto</MenuItem>
          </TextField>

          {/* Turnos */}
          {disableTurnos ? (
            <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
              En <b>{tipo_dia}</b> no se cargan turnos.
            </Typography>
          ) : (
            <Stack spacing={1.2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                  Turnos (mín 1 · máx 2)
                </Typography>

                <Button
                  size="small"
                  onClick={addTurno}
                  disabled={turnos.length >= 2}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Agregar
                </Button>
              </Stack>

              {(turnos || []).map((t, idx) => (
                <Stack
                  key={t.uid}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label="Inicio"
                    type="time"
                    value={t.hora_inicio}
                    onChange={(e) => updateTurno(idx, { hora_inicio: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                  />
                  <TextField
                    label="Fin"
                    type="time"
                    value={t.hora_fin}
                    onChange={(e) => updateTurno(idx, { hora_fin: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    fullWidth
                  />

                  <IconButton
                    onClick={() => removeTurno(idx)}
                    disabled={turnos.length <= 1}
                    sx={{ mt: 2.6 }}
                    title={turnos.length <= 1 ? "Debe quedar al menos 1 turno" : "Eliminar turno"}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">Cerrar</Button>

        <Button
          disabled={!canApply}
          variant="contained"
          onClick={() => {
            setErr("");

            const v = validate();
            if (v) {
              setErr(v);
              return;
            }

            const nextTipo = String(tipo_dia || "NORMAL").toUpperCase();

            const payload = {
              meta, // {horario_id, empleado_id, dia_semana}
              tipo_dia: nextTipo,
              tipo_horario: String(tipo_horario || "").toUpperCase(),
              turnos:
                nextTipo === "NORMAL"
                  ? (turnos || []).map((t) => ({
                      id: t.id ?? null, // si es null => es "nuevo" (crear)
                      hora_entrada: toHHMMSS(hhmm(t.hora_inicio)),
                      hora_salida: toHHMMSS(hhmm(t.hora_fin)),
                    }))
                  : (() => {
                      // ✅ Para FRANCO/NINGUNO necesitamos al menos 1 UPDATE (anchor) para setear tipo_dia
                      const anchor = (turnos || [])[0] || null;
                      const anchorId = anchor?.id ?? null;
                      return anchorId ? [{ id: anchorId }] : [];
                    })(),
              removed_ids: Array.from(new Set(removedIds || [])),
            };

            // Detectar "no changes"
            const orig = originalRef.current || {};
            const sameTipo = (orig.tipo_dia || "NORMAL") === payload.tipo_dia;
            const sameHorario = (orig.tipo_horario || "") === payload.tipo_horario;
            const sameTurnos = shallowEqTurnos(
              (orig.turnos || []).map((x) => ({ ...x })),
              (payload.turnos || []).map((x) => ({
                id: x.id,
                hora_inicio: hhmm(x.hora_entrada),
                hora_fin: hhmm(x.hora_salida),
              }))
            );
            const sameRemoved =
              JSON.stringify(orig.removed_ids || []) === JSON.stringify(payload.removed_ids || []);

            if (sameTipo && sameHorario && sameTurnos && sameRemoved) {
              onClose?.();
              return;
            }

            onConfirmUpdate?.(payload);
            onClose?.();
          }}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
